// Транспорт админки учёток (/api/admin/users*). Раздел выдаётся не по роли, а по
// учётке владельца — как касса; 403 здесь означает «не владелец», и это не поломка.
//
// ПРО ПАРОЛЬ. Он уходит В ТЕЛЕ POST по HTTPS — это штатный путь (так же ходит
// /api/login) — и НИКУДА больше: ни в query-строку, ни в журнал, ни в кэш react-query
// (мутация результат пароля не содержит — BFF возвращает учётку без хеша).
// Правило пароля — из mkuser.py: непустой и повтор совпадает; минимальной длины у
// mkuser нет, и здесь она не выдумана.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Unauthorized } from './client'

const БАЗА = '/api/admin/users'

/** Учётка в безопасном виде: хеша пароля BFF не отдаёт никогда, только признак. */
export interface Учётка {
  username: string
  name: string
  role: string
  /** Код набора разделов (рабочее место). null — набора нет, видно всё по роли. */
  set: string | null
  bitrix_user_id: number | null
  see_users: number[]
  see_depts: number[]
  disabled: boolean
  has_password: boolean
}

/** Запись заготовки mkusers_bulk: кого можно завести и почему кого-то нельзя. */
export interface ЗаписьЗаготовки {
  username: string
  name: string
  bitrix_user_id: number | null
  /** 'новая' — можно применить; иначе причина, почему нельзя (текст BFF). */
  status: string
}

export interface Обзор {
  users: Учётка[]
  draft: { entries: ЗаписьЗаготовки[]; total: number; applicable: number }
  /** Известные наборы разделов (rights.НАБОРЫ). Пусто — наборов в rights (ещё) нет. */
  sets?: { code: string; title: string }[]
}

/** Что учётки можно править. Ключи — ровно ПОЛЯ_ПРАВКИ из web/bff/admin_users.py. */
export interface Правка {
  role?: string
  /** Код набора; пустая строка снимает набор. */
  set?: string
  disabled?: boolean
  bitrix_user_id?: number | null
  see_users?: number[]
  see_depts?: number[]
  name?: string
}

async function запрос<T>(путь: string, init?: RequestInit): Promise<T> {
  const res = await fetch(БАЗА + путь, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 403) {
      throw new Error('раздел «Учётки и доступ» выдаётся не по роли, а по учётке '
        + 'владельца — как касса. Вам он не открыт.')
    }
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

const КЛЮЧ = ['admin-users'] as const

export function useУчётки() {
  return useQuery<Обзор>({
    queryKey: КЛЮЧ,
    queryFn: () => запрос<Обзор>(''),
    // Список учёток не кэшируем подолгу: правка соседней вкладкой должна быть видна.
    staleTime: 10_000,
    retry: 1,
  })
}

/** Общая обвязка мутаций: после успеха перечитать список. */
function useДействие<Т, Р>(ф: (арг: Т) => Promise<Р>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ф,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: КЛЮЧ }) },
  })
}

export function useПравка() {
  return useДействие((арг: { username: string; changes: Правка }) =>
    запрос<{ ok: boolean; user: Учётка }>('/update', {
      method: 'POST',
      body: JSON.stringify(арг),
    }))
}

export function useВыдачаПароля() {
  return useДействие((арг: { username: string; password: string; password2: string }) => {
    // Правило mkuser проверяем и на клиенте — чтобы человек увидел ошибку сразу,
    // а пустой пароль вовсе не ушёл в сеть. Сервер проверяет то же самое повторно.
    if (!арг.password) return Promise.reject(new Error('Пароль пустой.'))
    if (арг.password !== арг.password2) {
      return Promise.reject(new Error('Пароли не совпадают.'))
    }
    return запрос<{ ok: boolean; user: Учётка }>('/password', {
      method: 'POST',
      body: JSON.stringify(арг),
    })
  })
}

export function useПрименениеЗаготовки() {
  return useДействие((арг: { username?: string }) =>
    запрос<{ ok: boolean; applied: string[]; skipped: { username: string; why: string }[] }>(
      '/apply-draft', { method: 'POST', body: JSON.stringify(арг) }))
}

/** Роли CRM — те же, что rights.РОЛИ, и в том же порядке (от широкой к узкой). */
export const РОЛИ: { код: string; имя: string }[] = [
  { код: 'admin', имя: 'Администратор' },
  { код: 'finance', имя: 'Бухгалтерия' },
  { код: 'head', имя: 'Руководитель' },
  { код: 'user', имя: 'Сотрудник' },
]
